import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { Dayjs } from 'dayjs';
import { CLAIM_NETWORK_STATUSES } from 'types/Claims';
import {
	IBaseFilterState,
	IFilterState,
	IInputFilterState,
	sortingDirections,
} from 'types/Form';
import {
	getCarriersThunk,
	getClaimsStatusesThunk,
	getClientsThunk,
	getComplaintSourcesThunk,
	getComplaintStatusesThunk,
	getComplaintTypeThunk,
	getFraudStatusesThunk,
	getGroupsThunk,
	getNetworksThunk,
	getRelationshipTypesThunk,
	getReturnStatusesThunk,
} from './formThunk';

type Payload<T> = {
	[K in keyof T]: {
		name: K;
		value: T[K];
	};
}[keyof T];

const initialInputState: IInputFilterState = {
	inputFiscalCode: '',
	inputMemberId: '',
	inputFirstName: '',
	inputLastName: '',
	inputDateOfBirth: null,
	dateOfService: null,
	filterDateOfService: null,
	inputExternalClaimId: '',
	inputCarrierClaimId: '',
	inputClaimFirstName: '',
	inputClaimLastName: '',
	inputClaimStartDate: null,
	inputClaimEndDate: null,
	inputPaidStartDate: null,
	inputPaidEndDate: null,
	inputClaimStatus: '',
	inputReferenceNumber: null,
	inputTotalAmount: null,
	inputBillingId: '',
	inputBillingCarriers: ['All'],
	inputBillingMarkets: ['All'],
	inputClients: ['All'],
	inputGroups: ['All'],
	inputBillingTypologies: ['All'],
	inputBillingStatus: 'not_billed',
	inputBillingStartDate: '',
	inputBillingEndDate: '',
	inputClaimBillingStartDate: null,
	inputClaimBillingEndDate: null,
	inputClaimSettlementStartDate: null,
	inputClaimSettlementEndDate: null,
	inputClaimNumber: '',
	inputSettlementLettersCarriers: ['All'],
	inputSettlementLettersStartDate: '',
	inputSettlementLettersEndDate: '',
	sortingFields: 'externalClaimId,claimDate',
	sortingDirections: `${sortingDirections.DESC},${sortingDirections.DESC}`,
	carriers: [],
	groups: [],
	claimsStatuses: [],
	claimClients: [],
	claimClientsDomain: [],
	claimNetworkStatuses: CLAIM_NETWORK_STATUSES,
	claimReturnStatuses: [],
	networks: [],
	relationshipTypes: [],
	inputClient: '',
	inputClaimNetworkStatus: '',
	inputClaimReturnStatus: '',
	inputBillingNetworkStatus: '',
	inputHasComplaint: '',
	inputHasFraud: '',
	inputMemberLocation: '',
	inputClaimMarkets: ['All'],
	fraudStatuses: [],
	inputMemberBillingId: '',
	inputMemberBillingStatus: 'not_billed',
	inputMemberBillingMonthStart: null,
	inputMemberBillingMonthEnd: null,
	inputMemberClient: ['All'],
	inputMemberGroups: ['All'],
	inputMemberPlanCodes: ['All'],
	// Aggregation-specific duplicates (independent from member filters)
	inputAggregationBillingMonthStart: null,
	inputAggregationBillingMonthEnd: null,
	inputAggregationClient: ['All'],
	inputAggregationGroups: ['All'],
	inputAggregationPlanCodes: ['All'],
	inputMemberDivisionCode: '',
	inputMemberLastName: '',
	inputMemberFirstName: '',
	inputMemberRelationshipTypes: ['All'],
	inputMemberMemberId: '',
	inputMemberEmMemberId: '',
	inputMemberSubscriptionStartDate: null,
	inputMemberSubscriptionEndDate: null,
	inputFraudId: '',
	inputClaimId: '',
	inputDetectionDate: null,
	inputReferralDate: null,
	inputInvestigationStatus: '',
	complaintStatuses: [],
	complaintSources: [],
	complaintTypes: [],
	inputComplaintId: '',
	inputComplaintSource: '',
	inputComplaintType: '',
	inputComplaintNotificationDate: null,
	inputComplaintStatus: '',
	inputComplaintOutcome: '',
	inputComplaintClosureDate: null,
	inputFRLSent: '',
	inputFRLSentDate: null,
};

const initialBaseState: IBaseFilterState = {
	errorCode: 0,
	errorMessage: '',
	searchCheckElegibility: '',
	buttonSearchCheckElegibility: true,
	size: 10,
	search: false,
};

const initialState: IFilterState = {
	...initialBaseState,
	...initialInputState,
};
export const getCarriers = createAsyncThunk(
	'form/getCarriers',
	getCarriersThunk,
);

export const getGroups = createAsyncThunk('billing/getGroups', getGroupsThunk);

export const getClaimsStatuses = createAsyncThunk(
	'claims/getClaimsStatuses',
	getClaimsStatusesThunk,
);

export const getClients = createAsyncThunk(
	'billing/getClients',
	getClientsThunk,
);

export const getReturnStatuses = createAsyncThunk(
	'claim/getReturnStatuses',
	getReturnStatusesThunk,
);

export const getNetworks = createAsyncThunk(
	'form/getNetworks',
	getNetworksThunk,
);

export const getComplaintStatuses = createAsyncThunk(
	'form/getComplaintStatuses',
	getComplaintStatusesThunk,
);

export const getComplaintSources = createAsyncThunk(
	'form/getComplaintSources',
	getComplaintSourcesThunk,
);

export const getComplaintTypes = createAsyncThunk(
	'form/getComplaintTypes',
	getComplaintTypeThunk,
);

export const getFraudStatuses = createAsyncThunk(
	'form/getFraudStatuses',
	getFraudStatusesThunk,
);

export const getRelationshipTypes = createAsyncThunk(
	'form/getRelationshipTypes',
	getRelationshipTypesThunk,
);

const formSlice = createSlice({
	name: 'form',
	initialState,
	reducers: {
		handleSearchValuesForm: (state: IFilterState) => {
			state = { ...state };
		},
		handleSearchValueChange: (
			state: any,
			action: PayloadAction<React.ChangeEvent<HTMLInputElement>>,
		) => {
			const { name, value } = action.payload.target;
			state[name] = value;
		},
		handleChange: (
			state: any,
			action: PayloadAction<{
				name: string;
				value: string | Dayjs | null | string[] | boolean | undefined;
			}>,
		) => {
			state[action.payload.name] = action.payload.value;
		},
		setSearch: (state: IFilterState, { payload }: PayloadAction<boolean>) => {
			state.search = payload;
		},
		setSortingValues: (
			state: IFilterState,
			action: PayloadAction<{ name: string; value: string }>,
		) => {
			const { name, value } = action.payload;
			state.sortingFields = name;
			state.sortingDirections = value;
		},
		handleDateValueChange: (
			state: IFilterState,
			action: PayloadAction<
				Payload<
					Pick<
						IFilterState,
						'inputDateOfBirth' | 'dateOfService' | 'filterDateOfService'
					>
				>
			>,
		) => {
			const { name, value } = action.payload;

			if (name === 'dateOfService') state.dateOfService = value;
			if (name === 'inputDateOfBirth') state.inputDateOfBirth = value;
			if (name === 'filterDateOfService') state.filterDateOfService = value;
		},
		setDateOfService: (
			state: IFilterState,
			action: PayloadAction<Dayjs | null>,
		) => {
			state.dateOfService = action.payload;
		},
		resetDatesForm: (state: IFilterState) => {
			state.inputDateOfBirth = null;
		},
		checkSearch: (state: IFilterState) => {
			if (
				state.searchCheckElegibility === 'inputByFiscalCode' &&
				!!state.inputFiscalCode
			) {
				state.buttonSearchCheckElegibility = false;
			}
		},
		resetMemberFilters: state => ({
			...state,
			inputMemberBillingId: initialInputState.inputMemberBillingId,
			inputMemberBillingStatus: initialInputState.inputMemberBillingStatus,
			inputMemberBillingMonthStart: initialInputState.inputMemberBillingMonthStart,
			inputMemberBillingMonthEnd: initialInputState.inputMemberBillingMonthEnd,
			inputMemberClient: initialInputState.inputMemberClient,
			inputMemberGroups: initialInputState.inputMemberGroups,
			inputMemberPlanCodes: initialInputState.inputMemberPlanCodes,
			inputMemberDivisionCode: initialInputState.inputMemberDivisionCode,
			inputMemberLastName: initialInputState.inputMemberLastName,
			inputMemberFirstName: initialInputState.inputMemberFirstName,
			inputMemberRelationshipTypes: initialInputState.inputMemberRelationshipTypes,
			inputMemberMemberId: initialInputState.inputMemberMemberId,
			inputMemberEmMemberId: initialInputState.inputMemberEmMemberId,
			inputMemberSubscriptionStartDate:
				initialInputState.inputMemberSubscriptionStartDate,
			inputMemberSubscriptionEndDate:
				initialInputState.inputMemberSubscriptionEndDate,
		}),
		resetInputFilters: state => ({
			...state,
			...initialInputState,
			sortingFields: state.sortingFields,
			sortingDirections: state.sortingDirections,
			claimsStatuses: state.claimsStatuses,
			carriers: state.carriers,
			groups: state.groups,
			claimClients: state.claimClients,
			claimClientsDomain: state.claimClientsDomain,
			networks: state.networks,
			claimReturnStatuses: state.claimReturnStatuses,
			complaintStatuses: state.complaintStatuses,
			complaintSources: state.complaintSources,
			complaintTypes: state.complaintTypes,
			fraudStatuses: state.fraudStatuses,
			relationshipTypes: state.relationshipTypes,
		}),
		resetForm: state => ({
			...initialState,
			claimsStatuses: state.claimsStatuses,
			carriers: state.carriers,
			groups: state.groups,
			claimClients: state.claimClients,
			claimClientsDomain: state.claimClientsDomain,
			networks: state.networks,
			claimReturnStatuses: state.claimReturnStatuses,
			complaintStatuses: state.complaintStatuses,
			complaintSources: state.complaintSources,
			complaintTypes: state.complaintTypes,
			fraudStatuses: state.fraudStatuses,
			relationshipTypes: state.relationshipTypes,
		}),
	},
	extraReducers: builder => {
		builder
			.addCase(getCarriers.fulfilled, (state, { payload }) => {
				state.carriers = payload;
			})
			.addCase(getGroups.fulfilled, (state, { payload }) => {
				state.groups = payload;
			})
			.addCase(getCarriers.rejected, state => {
				state.carriers = [];
			})
			.addCase(getClaimsStatuses.fulfilled, (state, { payload }) => {
				state.claimsStatuses = [...payload].sort((a, b) => (a.id > b.id ? 1 : -1));
			})
			.addCase(getClaimsStatuses.rejected, state => {
				state.claimsStatuses = [];
			})
			.addCase(getClients.fulfilled, (state, { payload }) => {
				state.claimClients = payload;
				state.claimClientsDomain = payload;
			})
			.addCase(getReturnStatuses.fulfilled, (state, { payload }) => {
				state.claimReturnStatuses = payload;
			})
			.addCase(getNetworks.fulfilled, (state, { payload }) => {
				state.networks = payload;
			})
			.addCase(getNetworks.rejected, state => {
				state.networks = [];
			})
			.addCase(getComplaintStatuses.fulfilled, (state, { payload }) => {
				state.complaintStatuses = payload;
			})
			.addCase(getComplaintStatuses.rejected, state => {
				state.complaintStatuses = [];
			})
			.addCase(getComplaintSources.fulfilled, (state, { payload }) => {
				state.complaintSources = payload;
			})
			.addCase(getComplaintSources.rejected, state => {
				state.complaintSources = [];
			})
			.addCase(getComplaintTypes.fulfilled, (state, { payload }) => {
				state.complaintTypes = payload;
			})
			.addCase(getComplaintTypes.rejected, state => {
				state.complaintTypes = [];
			})
			.addCase(getFraudStatuses.fulfilled, (state, { payload }) => {
				state.fraudStatuses = payload;
			})
			.addCase(getFraudStatuses.rejected, state => {
				state.fraudStatuses = [];
			})
			.addCase(getRelationshipTypes.fulfilled, (state, { payload }) => {
				state.relationshipTypes = payload;
			})
			.addCase(getRelationshipTypes.rejected, state => {
				state.relationshipTypes = [];
			});
	},
});

export const {
	handleSearchValueChange,
	resetInputFilters,
	resetMemberFilters,
	resetForm,
	handleDateValueChange,
	setDateOfService,
	checkSearch,
	handleChange,
	resetDatesForm,
	setSearch,
	setSortingValues,
} = formSlice.actions;
export default formSlice.reducer;
